import { SECURITY } from '@atlassian/jira-issue-view-configurations/src/index.tsx';
import { normalizeAlias } from '@atlassian/jira-project-settings-apps-common/src/fields.tsx';
import {
	updateResponseToActiveLayoutItems,
	updateResponseItemTransformers,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/services/transformer/active-layout-items-from-update-response.tsx';
import type {
	ActiveFieldLayoutItem,
	FieldConfiguration,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/layout-items.tsx';
import {
	type SaveChangesServiceResponse,
	type UpdateIssueLayoutApiResponse,
	type FieldResponse as FieldOnServer,
	type ActiveLayoutItemOnServer,
	FIELD,
} from '@atlassian/jira-project-settings-apps-issue-type-page-product-features/src/common/types/save-changes.tsx';
import {
	JSD_FIELD_ALIAS,
	JSD_FIELD_APPROVAL_ALL_CONFIGS,
	JSD_FIELD_APPROVAL_CONFIG,
	JSD_FIELD_HELPTEXT,
} from '../constants.tsx';

const toField = (restField: FieldOnServer): ActiveFieldLayoutItem => {
	const field = updateResponseItemTransformers.toField(restField);
	const { properties } = restField.data;

	const configuration: FieldConfiguration = {
		...restField.data.configuration,
		inactiveFieldLozenge: undefined,
	};

	let alternateDescription;
	let alternateName;

	if (properties) {
		alternateName =
			typeof properties[JSD_FIELD_ALIAS] === 'string'
				? normalizeAlias(properties[JSD_FIELD_ALIAS])
				: undefined;
		alternateDescription =
			typeof properties[JSD_FIELD_HELPTEXT] === 'string'
				? normalizeAlias(properties[JSD_FIELD_HELPTEXT])
				: undefined;
		if (properties[JSD_FIELD_APPROVAL_CONFIG]) {
			configuration[JSD_FIELD_APPROVAL_CONFIG] = properties[JSD_FIELD_APPROVAL_CONFIG];
		}
		if (properties[JSD_FIELD_APPROVAL_ALL_CONFIGS]) {
			configuration[JSD_FIELD_APPROVAL_ALL_CONFIGS] = properties[JSD_FIELD_APPROVAL_ALL_CONFIGS];
		}
	}

	return {
		...field,
		data: {
			...field.data,
			alternateName,
			alternateDescription,
			configuration,
		},
	};
};

/**
 * 'Security Level' field is managed by Request Type Security dialog (@atlassian/jira-servicedesk-request-type-security) hence
 * when updating the issue layout we do not want it to show up on the page. All fields are returned when updating the issue layout.
 */
const filterSecurityLevelField = (
	activeLayoutItems: readonly ActiveLayoutItemOnServer[],
): readonly ActiveLayoutItemOnServer[] => {
	return activeLayoutItems.filter((activeLayoutItem) => {
		switch (activeLayoutItem.type) {
			case FIELD:
				return activeLayoutItem.key !== SECURITY;
			default:
				return true;
		}
	});
};

export const transformUpdateLayoutResponse = ({
	extraDefinerId,
	issueLayoutConfig,
	issueLayoutType,
	owners,
}: UpdateIssueLayoutApiResponse): SaveChangesServiceResponse => ({
	owners,
	extraDefinerId,
	issueLayoutType,
	name: owners[0].data.name,
	description: owners[0].data.description,
	activeLayoutItems: updateResponseToActiveLayoutItems(
		filterSecurityLevelField(issueLayoutConfig.items),
		{
			...updateResponseItemTransformers,
			toField,
		},
	),
});
